import { httpClient } from '@/shared/services';
import type {
  CreateLocalizationRunRequest,
  CreateLocalizationRunResponse,
  ExcelLocalizationOptions,
  LocalizationArtifactKind,
  LocalizationConfig,
  LocalizationRun,
} from '../types/localization.types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

function apiUrl(path: string): string {
  return `${API_BASE_URL.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
}

function idempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `localization-${Date.now()}-${Math.random()}`;
}

export const localizationService = {
  getConfig: async (): Promise<LocalizationConfig> => {
    const response = await httpClient.get<LocalizationConfig>('/v1/localization-runs/config');
    return response.data;
  },

  create: async (request: CreateLocalizationRunRequest): Promise<CreateLocalizationRunResponse> => {
    const response = await httpClient.post<CreateLocalizationRunResponse>(
      '/v1/localization-runs',
      request,
      { headers: { 'Idempotency-Key': idempotencyKey() } }
    );
    return response.data;
  },

  createFromExcel: async (
    file: File,
    options: ExcelLocalizationOptions
  ): Promise<CreateLocalizationRunResponse> => {
    const form = new FormData();
    form.append('file', file);
    form.append('sourceLocale', options.sourceLocale);
    form.append('targetLocales', JSON.stringify(options.targetLocales));
    form.append('force', String(options.force));
    form.append('replaceDefaultIndexado', String(options.replaceDefaultIndexado ?? false));
    form.append('additionalFields', JSON.stringify(options.additionalFields));
    const response = await httpClient.post<CreateLocalizationRunResponse, FormData>(
      '/v1/localization-runs',
      form,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Idempotency-Key': idempotencyKey(),
        },
      }
    );
    return response.data;
  },

  getRun: async (runId: string): Promise<LocalizationRun> => {
    const response = await httpClient.get<LocalizationRun>(`/v1/localization-runs/${runId}`);
    return response.data;
  },

  getModelCandidate: async (
    runId: string,
    sku: string,
    modelId: string
  ): Promise<Record<string, unknown>> => {
    const response = await httpClient.get<Record<string, unknown>>(
      `/v1/localization-runs/${runId}/candidates/${encodeURIComponent(sku)}/${encodeURIComponent(modelId)}`
    );
    return response.data;
  },

  selectModel: async (runId: string, sku: string, modelId: string): Promise<LocalizationRun> => {
    const response = await httpClient.post<LocalizationRun, { modelId: string }>(
      `/v1/localization-runs/${runId}/items/${encodeURIComponent(sku)}/select-model`,
      { modelId }
    );
    return response.data;
  },

  approve: async (runId: string): Promise<LocalizationRun> => {
    const response = await httpClient.post<LocalizationRun>(
      `/v1/localization-runs/${runId}/approve`
    );
    return response.data;
  },

  apply: async (runId: string): Promise<{ runId: string; status: LocalizationRun['status'] }> => {
    const response = await httpClient.post<{ runId: string; status: LocalizationRun['status'] }>(
      `/v1/localization-runs/${runId}/apply`
    );
    return response.data;
  },

  retryPending: async (runId: string): Promise<{
    runId: string;
    status: LocalizationRun['status'];
    enqueued: number;
  }> => {
    const response = await httpClient.post<{
      runId: string;
      status: LocalizationRun['status'];
      enqueued: number;
    }>(`/v1/localization-runs/${runId}/retry-pending`);
    return response.data;
  },

  retryPreviewFailed: async (runId: string): Promise<{
    runId: string;
    status: LocalizationRun['status'];
    enqueued: number;
  }> => {
    const response = await httpClient.post<{
      runId: string;
      status: LocalizationRun['status'];
      enqueued: number;
    }>(`/v1/localization-runs/${runId}/retry-preview-failed`);
    return response.data;
  },

  retryFailed: async (runId: string): Promise<{
    runId: string;
    status: LocalizationRun['status'];
    enqueued: number;
  }> => {
    const response = await httpClient.post<{
      runId: string;
      status: LocalizationRun['status'];
      enqueued: number;
    }>(`/v1/localization-runs/${runId}/retry-failed`);
    return response.data;
  },

  downloadArtifact: async (
    runId: string,
    sku: string,
    targetLocale: string,
    kind: LocalizationArtifactKind
  ): Promise<void> => {
    const blob = await httpClient.getBlob(
      kind === 'final'
        ? `/v1/localization-runs/${runId}/artifacts/${encodeURIComponent(sku)}/final`
        : `/v1/localization-runs/${runId}/artifacts/${encodeURIComponent(sku)}/${encodeURIComponent(targetLocale)}/${kind}`
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = kind === 'final'
      ? `${sku}.final.json`
      : `${sku}.${targetLocale}.${kind}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  },

  getArtifact: async (
    runId: string,
    sku: string,
    targetLocale: string,
    kind: LocalizationArtifactKind
  ): Promise<unknown> => {
    const blob = await httpClient.getBlob(
      kind === 'final'
        ? `/v1/localization-runs/${runId}/artifacts/${encodeURIComponent(sku)}/final`
        : `/v1/localization-runs/${runId}/artifacts/${encodeURIComponent(sku)}/${encodeURIComponent(targetLocale)}/${kind}`
    );
    return JSON.parse(await blob.text()) as unknown;
  },

  eventsUrl: (runId: string): string => apiUrl(`/v1/localization-runs/${runId}/events`),
};
